import React, { useState, useEffect, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  options, 
  label, 
  onAddNew,
  placeholder = "Adres ara...",
  error = false,
  errorMessage = ""
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const wrapperRef = useRef(null);
  
  // Initialize the input value based on the selected value
  useEffect(() => {
    if (value) {
      setInputValue(value);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Filter options based on input value
  useEffect(() => {
    if (!options || !options.length) {
      setFilteredOptions([]);
      return;
    }
    
    if (!inputValue) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => 
        (option.name && option.name.toLowerCase().includes(inputValue.toLowerCase())) ||
        (option.description && option.description.toLowerCase().includes(inputValue.toLowerCase())) ||
        (option.city && option.city.toLowerCase().includes(inputValue.toLowerCase())) ||
        (option.state && option.state.toLowerCase().includes(inputValue.toLowerCase())) ||
        (option.country && option.country.toLowerCase().includes(inputValue.toLowerCase()))
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  const handleOptionClick = (option) => {
    onChange(option.description);
    setInputValue(option.description);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {error && <span className="text-red-500">*</span>}
      </label>
      <div className="flex space-x-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            autoComplete="new-password"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {isOpen && filteredOptions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                >
                  <div className="font-medium">{option.name || option.description}</div>
                  <div className="text-sm text-gray-500">
                    {option.description && option.name && `${option.description} - `}
                    {option.city && `${option.city}, `}
                    {option.state && `${option.state}, `}
                    {option.country && option.country}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onAddNew}
          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <AddIcon className="w-4 h-4 mr-1" />
          Yeni Adres
        </button>
      </div>
      {error && errorMessage && (
        <p className="error-message text-red-500 text-xs mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
